import React, { useMemo, memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalBody, ModalHeader, ModalFooter } from 'reactstrap';
import moment from 'moment';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { ReactComponent as CloseButtonSvg } from 'assets/images/location/close-button-svg.svg';

import AppInputNumberValidation from 'components/App/InputNumberValidation';
import Button from 'components/App/Button';

import { parseCurrency } from 'utils';

import styles from './ModalEditPriceRoom.module.scss';

function ModalEditPriceRoom(props) {
  const {
    isOpen,
    data,
    onToggleModal,
    actionUpdateLocation,
    roomStatusList,
    place,
    dayStartFormat,
    dayEndFormat,
  } = props;

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      prices: data.prices || [],
    },

    validationSchema: Yup.object({
      prices: Yup.array().of(
        Yup.object({
          price: Yup.mixed()
            .transform((value, originalValue) => (
              originalValue === '' || originalValue === null || originalValue === undefined
                ? undefined
                : value
            ))
            .test(
              'required',
              'Vui lòng nhập giá',
              function requiredPrice() {
                const { originalValue } = this;
                return !(
                  originalValue === ''
                  || originalValue === null
                  || originalValue === undefined
                );
              },
            ),
        }),
      ),
    }),

    onSubmit: (values) => {
      const dataUpdate = {
        roomInfo: {
          id: place,
          roomId: data.room,
          roomStatusId: data._id,
        },
        newRoomStatus: {
          status: data.roomStatus || 'CLOSE',
          availableRooms: data.availableRooms || 0,
          prices: values.prices.map((item) => ({
            price: parseCurrency(item.price),
            priceType: item.priceType,
          })),
        },
        filter: {
          start: dayStartFormat,
          end: dayEndFormat,
          place,
        },
        callback: onToggleModal,
      };
      actionUpdateLocation(dataUpdate);
    },
  });

  const roomInfo = roomStatusList.find((item) => item._id === data.room);

  const findPriceName = useCallback(
    (id) => roomInfo?.priceTypes?.find((priceType) => priceType._id === id),
    [roomInfo],
  );

  const CloseButton = useMemo(
    () => (
      <button
        className={styles.ButtonClose}
        onClick={onToggleModal}
        type="button"
      >
        <CloseButtonSvg />
      </button>
    ),
    [onToggleModal],
  );

  const onPriceChange = useCallback(
    (id) => (e) => {
      validation.setValues((prevValues) => ({
        ...prevValues,
        prices: prevValues.prices.map((item) => {
          if (item._id === id) {
            return {
              ...item,
              price: e.target.value,
            };
          }
          return item;
        }),
      }));
    },
    [validation],
  );

  if (!roomInfo) {
    return null;
  }

  return (
    <div className={styles.Component}>
      <Modal
        fade
        size="lg"
        isOpen={isOpen}
        toggle={onToggleModal}
        className={styles.Modal}
      >
        <div className={styles.ModalHeaderWrapper}>
          <h2 className={styles.ModalHeaderTitle}>Cập nhật giá</h2>
          <ModalHeader
            toggle={onToggleModal}
            close={CloseButton}
            className={styles.ModalHeader}
          />
        </div>
        <ModalBody className={styles.ModalBody}>
          <div className="ServiceWrapper">
            <h3 className="ServiceWrapper__Title">{`Cập nhật nhanh giá phòng ${roomInfo.name.toUpperCase()} ngày ${moment(data.statusDate).format('DD-MM-YYYY')}`}</h3>

            {validation.values.prices.map((item, index) => (
              <div className="ModalEditRoom__FormField" key={item._id}>
                <span>
                  {`${findPriceName(item.priceType)?.name} :`}
                </span>

                <AppInputNumberValidation
                  name={`prices[${index}].price`}
                  keyName={`prices[${index}].price`}
                  id={`roomPrice-${item._id}`}
                  validation={validation}
                  isCustomOnChange
                  onChange={onPriceChange(item._id)}
                  onBlur={validation.handleBlur}
                  placeholder="Nhập số tiền"
                  showCurrency="VNĐ"
                  wrapperClassName="Input__Wrapper"
                  inputClassName="Input__Number"
                  value={item.price}
                />
              </div>
            ))}
          </div>
        </ModalBody>

        <ModalFooter className={styles.ModalFooter}>
          <div className="FooterButton">
            <div className="d-flex m-0 gap-2">
              <Button classes="Button Button__Cancel" onClick={onToggleModal}>
                Hủy
              </Button>

              <Button
                classes=" Button Button__Submit"
                onClick={validation.handleSubmit}
              >
                Xác nhận
              </Button>
            </div>
          </div>
        </ModalFooter>
      </Modal>
    </div>
  );
}

ModalEditPriceRoom.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  place: PropTypes.string,
  dayStartFormat: PropTypes.string,
  dayEndFormat: PropTypes.string,

  onToggleModal: PropTypes.func.isRequired,
  actionUpdateLocation: PropTypes.func.isRequired,

  roomStatusList: PropTypes.instanceOf(Array),
  data: PropTypes.instanceOf(Object),
};

ModalEditPriceRoom.defaultProps = {
  data: {},
  roomStatusList: [],
  place: '',
  dayStartFormat: '',
  dayEndFormat: '',
};

export default memo(ModalEditPriceRoom);
